import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    filterRowsBySearch,
    KpiCard,
    MetricBasis,
    MetricBasisToggle,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    TablePagination,
    TableSearch,
} from "./ReportingLab.jsx";

function promotionAmount(promotion, metricBasis) {
    return Number(metricBasis === MetricBasis.Completed ? promotion?.amountNet : promotion?.amountGross) || 0;
}

function promotionTurnover(promotion, metricBasis) {
    return metricBasis === MetricBasis.Completed ? promotion?.turnoverNet : promotion?.turnoverGross;
}

export default function ReportingDiscountsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [promotionData, setPromotionData] = useState({coupons: [], discounts: []});
    const [loading, setLoading] = useState(true);
    const [metricBasis, setMetricBasis] = useState(MetricBasis.Marketing);
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(25);
    const [tableSearch, setTableSearch] = useState("");
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Přijaté bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setPromotionData({coupons: [], discounts: []});

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.promotionPerformance({projectId: project.id, erpFilter})
            .then((nextPromotionData) => {
                if (!active) {
                    return;
                }

                setPromotionData({
                    coupons: nextPromotionData?.coupons ?? [],
                    discounts: nextPromotionData?.discounts ?? [],
                    ordersMarketing: nextPromotionData?.ordersMarketing ?? 0,
                    ordersCompleted: nextPromotionData?.ordersCompleted ?? 0,
                    turnoverMarketing: nextPromotionData?.turnoverMarketing,
                    turnoverCompleted: nextPromotionData?.turnoverCompleted,
                });
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const promotions = useMemo(() => [
        ...promotionData.coupons.map((coupon) => ({
            ...coupon,
            type: "coupon",
            label: coupon.code ?? "-",
        })),
        ...promotionData.discounts.map((discount) => ({
            ...discount,
            type: "discount",
            label: discount.name || discount.code || "-",
        })),
    ].filter((promotion) => promotionAmount(promotion, metricBasis) > 0 || moneyAmount(promotionTurnover(promotion, metricBasis)) > 0)
        .sort((a, b) => promotionAmount(b, metricBasis) - promotionAmount(a, metricBasis)), [promotionData, metricBasis]);
    const promotionTurnoverSummary = metricBasis === MetricBasis.Completed ? promotionData.turnoverCompleted : promotionData.turnoverMarketing;
    const promotionTurnoverTotal = moneyAmount(promotionTurnoverSummary);
    const promotionUsageTotal = metricBasis === MetricBasis.Completed ? promotionData.ordersCompleted : promotionData.ordersMarketing;
    const firstPromotionWithCurrency = promotions.find((promotion) => promotionTurnover(promotion, metricBasis)?.currency);
    const currency = promotionTurnoverSummary?.currency ?? promotionTurnover(firstPromotionWithCurrency, metricBasis)?.currency ?? projectCurrency(project);
    const topPromotion = promotions[0];
    const topPromotions = promotions.slice(0, 10);
    const filteredPromotions = filterRowsBySearch(promotions, tableSearch, (promotion) => promotion.label);
    const tablePageCount = Math.max(1, Math.ceil(filteredPromotions.length / tablePageSize));
    const safeTablePage = Math.min(tablePage, tablePageCount);
    const visiblePromotions = filteredPromotions.slice((safeTablePage - 1) * tablePageSize, safeTablePage * tablePageSize);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const couponUsageOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 18, bottom: 28, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...topPromotions].reverse().map((promotion) => promotion.label),
            axisLabel: {fontSize: 12, width: 260, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.discounts.usage", {defaultValue: "Použití"}),
                type: "bar",
                data: [...topPromotions].reverse().map((promotion, index) => ({
                    value: promotionAmount(promotion, metricBasis),
                    itemStyle: {color: ChannelMixColors[(topPromotions.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };

    const couponTurnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: topPromotions.slice(0, 8).map((promotion) => ({
                    name: promotion.label,
                    value: moneyToMajor(promotionTurnover(promotion, metricBasis)),
                })),
            },
        ],
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.discounts", {defaultValue: "Slevy a kupony"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.discounts.title", {defaultValue: "K čemu slouží slevy a kupony"})}
                    actions={<MetricBasisToggle value={metricBasis} onChange={setMetricBasis} metricBasisMeta={metricBasisMeta}/>}
                >
                    {t("reporting_lab.descriptions.discounts.text", {defaultValue: "Sleduje využití kuponů i řádkových nebo automatických slev na jednom místě. Aktuální data ukazují počet objednávek s promo prvkem a jejich obrat, ne nominální hodnotu poskytnuté slevy."})}
                </ReportingPageDescription>

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.discounts.used_coupons", {defaultValue: "Použitá promo"})} value={renderNumber(promotionUsageTotal)} meta={t("reporting_lab.discounts.unique_codes", {defaultValue: "Unikátní položky"}) + `: ${renderNumber(promotions.length)}`}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.discounts.coupon_revenue", {defaultValue: "Obrat objednávek s promo"})} value={renderCurrencyValue({amount: promotionTurnoverTotal, currency}, true)} meta={metricBasisMeta[metricBasis]?.shortLabel}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.discounts.top_coupon", {defaultValue: "Nejsilnější promo"})} value={topPromotion?.label ?? "-"} meta={topPromotion ? `${renderNumber(promotionAmount(topPromotion, metricBasis))}x` : "-"}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.discounts.coupon_usage", {defaultValue: "Použití promo prvků"})} className="col-span-12 xl:col-span-7">
                    <EChart option={couponUsageOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.discounts.coupon_revenue_share", {defaultValue: "Rozpad obratu promo prvků"})} className="col-span-12 xl:col-span-5">
                    <EChart option={couponTurnoverOption} className="h-96"/>
                </Panel>

                <Panel
                    loading={loading}
                    title={t("reporting_lab.discounts.coupon_table", {defaultValue: "Přehled slev a kuponů"})}
                    className="col-span-12"
                >
                    <TableSearch value={tableSearch} onChange={(value) => { setTableSearch(value); setTablePage(1); }} visible={promotions.length > 15} t={t}/>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.discounts.coupon", {defaultValue: "Promo"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.discounts.type", {defaultValue: "Typ"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.discounts.usage", {defaultValue: "Použití"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {visiblePromotions.map((promotion) => <tr key={`${promotion.type}-${promotion.label}-${promotionAmount(promotion, metricBasis)}`}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{promotion.label}</td>
                                <td className="py-3 pr-4 text-slate-600 dark:text-slate-300">{promotion.type === "coupon" ? t("reporting_lab.discounts.type_coupon", {defaultValue: "Kupon"}) : t("reporting_lab.discounts.type_discount", {defaultValue: "Sleva"})}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(promotionAmount(promotion, metricBasis))}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(promotionTurnover(promotion, metricBasis), true)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination page={safeTablePage} pageSize={tablePageSize} totalItems={filteredPromotions.length} onPageChange={setTablePage} onPageSizeChange={(value) => { setTablePageSize(value); setTablePage(1); }} t={t}/>
                    {promotions.length === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.discounts.empty", {defaultValue: "Pro vybrané období nejsou dostupná data o slevách nebo kuponových kódech."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
